import React, { useLayoutEffect, useRef } from 'react';
import { View, TouchableOpacity, StyleSheet, ViewStyle, StyleProp } from 'react-native';
import Animated from 'react-native-reanimated';
import { useAccordionAnimation } from '@/hooks/common/useAccordionAnimation';

interface AnimatedAccordionProps {
  isExpanded: boolean;
  /** When false, expand/collapse changes apply instantly (no animation). Defaults to true. */
  animate?: boolean;
  onToggle: () => void;
  header: React.ReactNode;
  chevron: React.ReactNode;
  children: React.ReactNode;
  containerStyle?: StyleProp<ViewStyle>;
  headerStyle?: StyleProp<ViewStyle>;
  contentStyle?: StyleProp<ViewStyle>;
  accessibilityLabel?: string;
  accessibilityHint?: string;
}

export const AnimatedAccordion: React.FC<AnimatedAccordionProps> = ({
  isExpanded,
  animate = true,
  onToggle,
  header,
  chevron,
  children,
  containerStyle,
  headerStyle,
  contentStyle,
  accessibilityLabel,
  accessibilityHint,
}) => {
  const { setOpen, setOpenImmediate, animatedHeightStyle, animatedChevronStyle, onContentLayout } =
    useAccordionAnimation({ initiallyOpen: isExpanded });

  // Use a ref so `animate` changes alone don't re-fire the effect
  const animateRef = useRef(animate);
  animateRef.current = animate;

  useLayoutEffect(() => {
    if (animateRef.current) {
      setOpen(isExpanded);
    } else {
      setOpenImmediate(isExpanded);
    }
  }, [isExpanded, setOpen, setOpenImmediate]);

  return (
    <View style={containerStyle}>
      <TouchableOpacity
        style={headerStyle}
        onPress={onToggle}
        activeOpacity={0.7}
        accessibilityLabel={accessibilityLabel}
        accessibilityHint={accessibilityHint}
        accessibilityRole="button"
        accessibilityState={{ expanded: isExpanded }}
      >
        {header}
        <Animated.View style={animatedChevronStyle}>
          {chevron}
        </Animated.View>
      </TouchableOpacity>
      <Animated.View style={animatedHeightStyle}>
        <View onLayout={onContentLayout} style={styles.measureWrapper}>
          <View style={contentStyle}>
            {children}
          </View>
        </View>
      </Animated.View>
    </View>
  );
};

const styles = StyleSheet.create({
  measureWrapper: {
    position: 'absolute',
    width: '100%',
  },
});
